import React, { useState } from 'react';
import { X, CheckCircle, MapPin, Briefcase } from 'lucide-react';
import { useAdvisorStore } from '../../store/advisorStore';
import type { AdvisorManagement, AdvisorRequest, AdvisorApprovalForm } from '../../types/advisor';

interface AdvisorApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  advisor: AdvisorManagement | AdvisorRequest;
  onSuccess: () => void;
}

export const AdvisorApprovalModal: React.FC<AdvisorApprovalModalProps> = ({
  isOpen,
  onClose,
  advisor,
  onSuccess
}) => {
  const { territories, sectors, approveRequest, isLoading } = useAdvisorStore();
  const [formData, setFormData] = useState<AdvisorApprovalForm>({
    territory_id: '',
    sector: '',
    notes: '',
    notify_coordinators: true
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await approveRequest(advisor.id, formData);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'approbation');
    }
  };

  const handleChange = (field: keyof AdvisorApprovalForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Approuver la demande</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Advisor Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Informations du candidat</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Nom complet</p>
                <p className="font-medium text-gray-900">{advisor.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium text-gray-900">{advisor.email}</p>
              </div>
              {advisor.phone && (
                <div>
                  <p className="text-sm text-gray-600">Téléphone</p>
                  <p className="font-medium text-gray-900">{advisor.phone}</p>
                </div>
              )}
              {advisor.organization && (
                <div>
                  <p className="text-sm text-gray-600">Organisation</p>
                  <p className="font-medium text-gray-900">{advisor.organization}</p>
                </div>
              )}
            </div>
          </div>

          {/* Territory and Sector */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="territory" className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Territoire d'intervention
              </label>
              <select
                id="territory"
                value={formData.territory_id}
                onChange={(e) => handleChange('territory_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Sélectionnez un territoire</option>
                {territories.map((territory) => (
                  <option key={territory.id} value={territory.id}>
                    {territory.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="sector" className="block text-sm font-medium text-gray-700 mb-2">
                <Briefcase className="w-4 h-4 inline mr-1" />
                Secteur d'expertise
              </label>
              <select
                id="sector"
                value={formData.sector}
                onChange={(e) => handleChange('sector', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Sélectionnez un secteur</option>
                {sectors.map((sector) => (
                  <option key={sector.id} value={sector.name}>
                    {sector.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Notes internes (optionnel)
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Commentaires sur l'approbation..."
            />
          </div>

          {/* Notification Option */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="notify_coordinators"
              checked={formData.notify_coordinators}
              onChange={(e) => handleChange('notify_coordinators', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="notify_coordinators" className="text-sm text-gray-700">
              Notifier tous les coordinateurs de cette approbation
            </label>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Approbation...</span>
                </div>
              ) : (
                'Approuver la demande'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};